/**
 * What the HUD shows, written by the GameController and read by the Preact components.
 *
 * Slow-changing state (level, attempt, inventory, overlays) lives in signals: the components
 * re-render only when it changes. Fast-changing numbers (race timer, level progress, the
 * progress strip, boost timers) are written straight into DOM nodes (hudDom.ts), a few
 * times a second at most, so the HUD never re-renders at 60 fps.
 */
import type { AvatarId, BoostType, BossKind, PlayerId } from '@cube-rush/shared';
import { type ReadonlySignal, type Signal, signal } from '@preact/signals';
import type { PadStyle } from '../../input/pads';
import type { Rect } from '../layout';

/**
 * countdown: before the start; racing; finished: the local race is done; timeUp: the time of a
 * race against the clock is up (the run stands still); spectating.
 */
export type HudPhase = 'countdown' | 'racing' | 'finished' | 'timeUp' | 'spectating';

export interface FinishInfo {
  timeMs: number;
  /** 1-based, once the server confirms the finish time. */
  place: number | null;
  /** Somebody is still racing. */
  waiting: boolean;
}

/** «Время вышло!»: the end of a race against the clock. */
export interface TimeUpInfo {
  /** Levels the local player completed in time; null for spectators. */
  levels: number | null;
  /** 1-based, once the server has every level the local player completed. */
  place: number | null;
}

/** The race strip of a race against the clock spans at least this many levels. */
export const MIN_STRIP_SPAN = 3;

/** A racer on the progress strip. */
export interface StripRacer {
  id: PlayerId;
  avatar: AvatarId;
  name: string;
  /** Ring colour (the racer's cube accent). */
  color: string;
  me: boolean;
  finished: boolean;
  quit: boolean;
  offline: boolean;
  /** Completed levels: the score of a race against the clock (null in a race by levels). */
  levels: number | null;
}

/** The name label of a view that follows another racer (split screen). */
export interface ViewLabel {
  key: string;
  id: PlayerId;
  avatar: AvatarId;
  name: string;
  /** 1-based level the racer is on. */
  level: number;
  /** No news for a while (offline). */
  stale: boolean;
  /** Crossed the last finish line. */
  finished: boolean;
  /** Where the view is, CSS px in the game area. */
  rect: Rect;
}

export interface Notice {
  key: number;
  avatar: AvatarId;
  name: string;
  text: string;
}

export interface ActiveBoostView {
  type: BoostType;
}

/**
 * The banner of a whole level at its start («Шарик!», «Стрелочка!», «Робот!», «Паук!»), of the
 * robot or the spider at their portal in a cube level, or of a boss level at its start («Босс!»,
 * `boss`: which); `hint`: how to play it, the first time in the race. `badge`: the title alone,
 * small, along the top bar, out of the play's way (the robot's or the spider's first portal in a
 * race where the section after it is built as any other). `key` restarts its animation.
 */
export interface ModeIntro {
  key: number;
  mode: 'ball' | 'wave' | 'robot' | 'spider' | 'boss';
  hint: boolean;
  boss?: BossKind;
  badge?: boolean;
}

export class HudModel {
  readonly phase: Signal<HudPhase>;
  /** Seconds to go (3, 2, 1; more = "get ready"), 0 = "Старт!", null = no countdown. */
  readonly countdown = signal<number | null>(null);
  /** 0-based level of the local player (or of the watched racer). */
  readonly level = signal(0);
  readonly attempt = signal(1);
  readonly inventory = signal<ReadonlyArray<BoostType | null>>([null, null]);
  readonly activeBoosts = signal<readonly BoostType[]>([]);
  /** Boosts can be used right now (racing and alive). */
  readonly boostsReady = signal(false);
  /** "Level N complete" banner; `key` restarts its animation. */
  readonly levelFlash = signal<{ key: number; level: number } | null>(null);
  /** A mode's banner (see ModeIntro). */
  readonly modeIntro = signal<ModeIntro | null>(null);
  readonly finish = signal<FinishInfo | null>(null);
  /** A race against the clock is over: «Время вышло!» (racers and spectators alike). */
  readonly timeUp = signal<TimeUpInfo | null>(null);
  /** The last seconds of a race against the clock: the timer turns red. */
  readonly hurry = signal(false);
  readonly menuOpen = signal(false);
  readonly confirmQuit = signal(false);
  readonly notices = signal<readonly Notice[]>([]);
  readonly rotateHint = signal(false);
  readonly labels = signal<readonly ViewLabel[]>([]);
  /** Spectators in single view: the racer being watched (null: nobody to watch). */
  readonly watching = signal<PlayerId | null>(null);
  /** More than one racer to switch between. */
  readonly canSwitch = signal(false);
  readonly strip = signal<readonly StripRacer[]>([]);
  /** The main view's rectangle (boost buttons sit in its corner). */
  readonly mainRect = signal<Rect>({ x: 0, y: 0, w: 0, h: 0 });
  /**
   * How many levels the race strip spans: the race's levels, or against the clock a track as
   * long as the race so far (it grows as the leader goes on).
   */
  readonly stripSpan: Signal<number>;
  /** The level to count to (the badge's «/N»); null against the clock. */
  readonly levelTotal: number | null;

  constructor(
    /** Levels of the race (against the clock: more than anybody can complete). */
    readonly levelCount: number,
    readonly spectator: boolean,
    /** Length of a race against the clock (ms); null for a race by levels. */
    readonly durationMs: number | null = null,
    /**
     * How the gamepad used last names its buttons (null: no pad used yet): the boost buttons
     * and the countdown's hint name the pad's buttons instead of the keys.
     */
    readonly padStyle: ReadonlySignal<PadStyle | null> = signal(null),
  ) {
    this.phase = signal<HudPhase>(spectator ? 'spectating' : 'countdown');
    this.levelTotal = durationMs === null ? levelCount : null;
    this.stripSpan = signal(durationMs === null ? levelCount : MIN_STRIP_SPAN);
  }

  /** A race against the clock: most levels when the time is up wins. */
  get timed(): boolean {
    return this.durationMs !== null;
  }
}
